<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="fields"
      :table-filter="{ external: true, placeholder: 'ค้นหาเลขที่ใบงาน, รายละเอียด...', label: 'ค้นหา:' }"
      :table-filter-value="searchKeyword"
      @update:table-filter-value="onSearchChange"
      hover
      :pagination="false"
      :loading="isLoading"
      :items-per-page-select="{
        label: $t('table.common.itemsPerPage') || 'แสดงต่อหน้า:',
        values: [10, 20, 50],
        external: true
      }"
      :items-per-page="pageSize"
      @pagination-change="onPageSizeChange"
    >
      
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #reported_datetime="{ item }">
        <td>
          <div>{{ formatDate(item.userPart.dateTimeInformed) }}</div>
        </td>
      </template>

      <template #username="{ item }">
        <td>
          <div class="font-weight-bold">{{ item.username }}</div>
          <div v-if="item.onBehalfOf" class="small text-danger">
            (แจ้งแทน: {{ item.onBehalfOf }})
          </div>
          <div class="small text-muted">{{ item.userEmail }}</div>
        </td>
      </template>

      <template #issue_details="{ item }">
        <td>
          <div>
            <span class="text-primary font-weight-bold">{{ getCategoryName(item) }}</span>
            <br />
            <span class="text-muted">{{ getSubCategoryName(item) }}</span>
          </div>
        </td>
      </template>

      <template #rating="{ item }">
        <td class="text-center">
          <div style="white-space: nowrap;">
            <span
              v-for="n in 5"
              :key="n"
              style="font-size: 18px; margin: 0 1px;"
              :style="{ color: n <= getRatingStars(item) ? '#FFD700' : '#E4E5E9' }"
            >
              ★
            </span>
          </div>
        </td>
      </template>

      <template #actions="{ item }">
        <td class="text-center">
          <CButton color="info" variant="outline" size="sm" class="circle-btn" @click="onRowClick(item)"
            title="ดูรายละเอียด">
            <CIcon name="cil-magnifying-glass" size="sm" />
          </CButton>
        </td>
      </template>

    </CDataTable>
    <CPagination
      v-show="totalPages > 1"
      :pages="totalPages"
      :active-page.sync="currentPage"
      :double-arrows="false"
      align="center"
    />
    
  </CCardBody>
</template>

<style>
/* CSS คงเดิม */
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}

.custom-pagination .page-item .page-link {
  color: #414141;
}

.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}

.circle-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>

<script>
import axios from "axios";
import { getSocket } from "../../service/socketio.service";

// ✅ แก้ไข fields: เพิ่ม ticketNumber
const fields = [
  { key: "ticketNumber", label: "เลขที่ใบแจ้งซ่อม", _style: "width:15%;" },
  { key: "reported_datetime", label: "วัน/เวลา แจ้ง", _style: "width:15%;" },
  { key: "username", label: "ผู้แจ้ง", _style: "width:20%;" },
  { key: "issue_details", label: "รายละเอียด", _style: "width:30%;" },
  {
    key: "rating",
    label: "ความพึงพอใจ",
    _style: "width:15%; text-align:center;",
    class: "text-center"
  },
  { key: "actions", label: "", _style: "width:5%;", sorter: false, filter: false },
];

export default {
  name: "Ahistorytable",
  data() {
    const saved = Number(localStorage.getItem('smartfix_page_size'));
    const initialPageSize = [10, 20, 50].includes(saved) ? saved : 50;
    return {
      ticketsData: [],
      fields,
      isLoading: false,
      hasLoaded: false,
      currentPage: 1,
      pageSize: initialPageSize,
      totalItems: 0,
      searchKeyword: "",
      searchTimeout: null,
      isMounted: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize) || 1;
    },
  },
  mounted() {
    // โหลดประวัติเฉพาะเมื่อเปิดแท็บหรือถูกเรียกโดย parent component เพื่อความเร็วสูงสุด
    this.isMounted = true;
  },
  watch: {
    currentPage() {
      if (this.isMounted) this.loadHistory();
    },
  },
  methods: {
    onPageSizeChange(newSize) {
      const size = Number(newSize);
      if ([10, 20, 50].includes(size) && size !== this.pageSize) {
        this.pageSize = size;
        localStorage.setItem('smartfix_page_size', String(size));
        this.currentPage = 1;
        this.loadHistory();
      }
    },

    onSearchChange(val) {
      this.searchKeyword = val || "";
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadHistory();
      }, 350);
    },

    async loadHistory() {
      try {
        this.isLoading = true;
        const params = {
          status: 'history',
          page: this.currentPage,
          limit: this.pageSize,
        };
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.search = this.searchKeyword.trim();
        }

        const response = await axios.get(`${process.env.VUE_APP_APP_URL}/ticket/admin/all`, { params });

        if (response.data.success) {
          const allTickets = response.data.data || [];

          const mappedTickets = allTickets.map(ticket => {
            const user = ticket.userPart?.user || {};
            return {
              ...ticket,
              username: `${user.name || ''} ${user.surname || ''}`.trim() || 'ไม่ระบุตัวตน',
              onBehalfOf: ticket.userPart?.onBehalfOf || '',
              userEmail: user.email || '-'
            };
          });

          this.ticketsData = (this.searchKeyword && this.searchKeyword.trim())
            ? mappedTickets
            : mappedTickets.filter(t => t.status === 'welldone');
          this.totalItems = response.data.pagination?.total ?? this.ticketsData.length;
          this.hasLoaded = true;
        }
      } catch (error) {
        console.error("Failed to load admin history:", error);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes} น.`;
    },
    getCategoryName(item) {
      return item.userPart?.category?.departmentName || "ไม่ระบุ";
    },

    getSubCategoryName(item) {
      const sub = item.userPart?.subCategory;
      
      if (!sub) return "-";

      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }

      return sub;
    },

    getRatingStars(item) {
      return item.rating?.stars || 0;
    },
    onRowClick(item) {
      this.$emit("show-detail", item);
    },
  },
};
</script>
